import type { PageWithAccess, SectionWithAccess } from "./sectionAccessService";

import type { NavPage, NavSection } from "@/features/manual/api";

/**
 * The sidebar's manual tree doubles as the management surface for
 * cms.manage holders. Nav data (/api/cms/nav) is filtered to what the
 * caller can read; the admin list (/api/cms/sections) is the complete,
 * authoritative set with role access. Merging matters for two reasons:
 * reorder is a full-order replace (a partial id set 409s), and an admin
 * who restricts a section away from their own roles must still see it
 * here to manage it back — otherwise it's a lockout.
 */

export interface ManagedSection {
  id: string;
  slug: string;
  title: string;
  ordinal: number;
  restricted: boolean;
  /** In the caller's own nav. False = manage-only: dimmed, not navigable. */
  readable: boolean;
  /** Role uuids allowed to see the section; null until admin data loads. */
  roleIds: string[] | null;
  pages: NavPage[];
}

export interface ManagedPage {
  id: string;
  slug: string;
  title: string;
  /** Order among siblings (navTree.ts). */
  ordinal: number;
  parentPageId: string | null;
  /** null = not in the caller's nav, so unknown (no draft tag shown). */
  published: boolean | null;
  /** In the caller's own nav. False = manage-only: dimmed, not navigable. */
  readable: boolean;
  /** Role uuids that further restrict the page; null until admin data loads. */
  roleIds: string[] | null;
}

/**
 * Merge the caller-visible nav tree with the admin section list. Order and
 * ordinals come from the admin list when present (it is complete); sections
 * missing from nav become non-readable rows. Returns null while nav is
 * still loading so the caller keeps its loading state.
 */
export function mergeSections(
  nav: NavSection[] | null,
  admin: SectionWithAccess[] | null
): ManagedSection[] | null {
  if (!nav) return null;
  if (!admin) {
    return nav.map((s) => ({
      id: s.id,
      slug: s.slug,
      title: s.title,
      ordinal: s.ordinal,
      restricted: s.restricted,
      readable: true,
      roleIds: null,
      pages: s.pages,
    }));
  }
  const navById = new Map(nav.map((s) => [s.id, s]));
  const adminIds = new Set(admin.map((s) => s.id));
  const merged: ManagedSection[] = admin.map((s) => {
    const seen = navById.get(s.id);
    return {
      id: s.id,
      slug: s.slug,
      title: s.title,
      ordinal: s.ordinal,
      restricted: s.roleIds.length > 0,
      readable: seen !== undefined,
      roleIds: s.roleIds,
      pages: seen?.pages ?? [],
    };
  });
  // A section present in nav but not yet in the (cached) admin list — e.g.
  // created after admin data was fetched. Keep it visible with its real
  // ordinal so it isn't dropped and so a reorder still sends the full id set.
  for (const s of nav) {
    if (adminIds.has(s.id)) continue;
    merged.push({
      id: s.id,
      slug: s.slug,
      title: s.title,
      ordinal: s.ordinal,
      restricted: s.restricted,
      readable: true,
      roleIds: null,
      pages: s.pages,
    });
  }
  return merged.sort((a, b) => a.ordinal - b.ordinal);
}

/**
 * Merge a section's caller-visible nav pages with the admin page list
 * (complete, ordered, with role access). Same doctrine as mergeSections.
 */
export function mergePages(navPages: NavPage[], admin: PageWithAccess[] | null): ManagedPage[] {
  if (!admin) {
    return navPages.map((p) => ({
      id: p.id,
      slug: p.slug,
      title: p.title,
      ordinal: p.ordinal,
      parentPageId: p.parentPageId ?? null,
      published: p.published,
      readable: true,
      roleIds: null,
    }));
  }
  const navById = new Map(navPages.map((p) => [p.id, p]));
  const adminIds = new Set(admin.map((p) => p.id));
  const merged: ManagedPage[] = admin.map((p) => {
    const seen = navById.get(p.id);
    return {
      id: p.id,
      slug: p.slug,
      title: p.title,
      ordinal: p.ordinal,
      parentPageId: p.parentPageId ?? seen?.parentPageId ?? null,
      published: seen ? seen.published : null,
      readable: seen !== undefined,
      roleIds: p.roleIds,
    };
  });
  // A page in nav but not in the (cached) admin list — e.g. just created in
  // this section. Keep it (roleIds unknown until the admin list refetches) so
  // it shows in the tree and reorder submits the complete id set.
  for (const p of navPages) {
    if (adminIds.has(p.id)) continue;
    merged.push({
      id: p.id,
      slug: p.slug,
      title: p.title,
      ordinal: p.ordinal,
      parentPageId: p.parentPageId ?? null,
      published: p.published,
      readable: true,
      roleIds: null,
    });
  }
  return merged.sort((a, b) => a.ordinal - b.ordinal);
}
